/**
 * Dependency-free PostHog client for the browser.
 *
 * POSTs events to PostHog's capture endpoint directly (same raw-HTTP approach as
 * the server's AnalyticsService). No posthog-js — it can't be added here (pnpm
 * install is broken) and this keeps the bundle small. The trade-off vs the SDK is
 * no autocapture; we send explicit pageviews + identify + named events, which is
 * the useful core. Upgrading to posthog-js later (for autocapture/session replay)
 * is a drop-in replacement of this module.
 *
 * INERT until `VITE_POSTHOG_KEY` is set: no key ⇒ every call is a no-op.
 */

const ANON_ID_STORAGE_KEY = "morrow_ph_distinct_id";

let apiKey: string | null = null;
let host = "https://us.i.posthog.com";
let distinctId: string | null = null;

function anonId(): string {
  try {
    const existing = localStorage.getItem(ANON_ID_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    // storage blocked — fall through to an ephemeral id
  }
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  const id = `anon_${Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("")}`;
  try {
    localStorage.setItem(ANON_ID_STORAGE_KEY, id);
  } catch {
    // ignore
  }
  return id;
}

export function initPostHog(opts: {
  readonly key?: string | undefined;
  readonly host?: string | undefined;
}): void {
  apiKey = opts.key || null;
  if (opts.host) host = opts.host.replace(/\/$/, "");
  if (apiKey) distinctId = anonId();
}

export function posthogEnabled(): boolean {
  return apiKey !== null;
}

function send(event: string, properties?: Readonly<Record<string, unknown>>): void {
  if (!apiKey || !distinctId) return;
  try {
    void fetch(`${host}/capture/`, {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        api_key: apiKey,
        event,
        distinct_id: distinctId,
        properties: {
          ...properties,
          ...(typeof location !== "undefined" ? { $current_url: location.href } : {}),
          surface: "web",
        },
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {
    // telemetry must never throw into the app
  }
}

/** Bind subsequent events to the signed-in user (Clerk id) and set person props. */
export function identify(userId: string, props?: Readonly<Record<string, unknown>>): void {
  if (!apiKey || !userId) return;
  distinctId = userId;
  send("$identify", props ? { $set: props } : undefined);
}

/** Reset to a fresh anonymous id (call on sign-out). */
export function resetIdentity(): void {
  if (!apiKey) return;
  try {
    localStorage.removeItem(ANON_ID_STORAGE_KEY);
  } catch {
    // ignore
  }
  distinctId = anonId();
}

export function capture(event: string, properties?: Readonly<Record<string, unknown>>): void {
  send(event, properties);
}

export function pageview(): void {
  send("$pageview");
}
